import {
  asRangeInclusive,
  concretiseParams,
  updateSearchParamsWithFilters,
} from "../filters/filters"
import { updateSearchParamsWithPagination } from "../../page"
import { API_BASE_URL } from "../../app/config"
import { getToken, handleSignIn } from "../iam/middleware"

/**
 * Get latest Observations (ordered by timestamp)
 * @param filers  // filters to apply
 * @param page  // pagination settings
 * @returns {Promise<any>}
 */
export const getObservations = async ({ filter = {}, page = {} }) => {
  const params = observationListSearchParams(filter)
  updateSearchParamsWithPagination(params, page)
  let resp = await fetch(
    `${API_BASE_URL}/api/observations?${params.toString()}`,
    {
      method: "GET",
      mode: "cors",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      redirect: "error",
    }
  )
  resp = handleSignIn(resp)
  return resp.json()
}

// In the given URLSearchParams object, replaces a human-friendly "object_count",
// in a format like ">=3", "<9", "8 - 10" and "17", with discrete
// "solve_count_gt" and "solve_count_lt" values.
const expandObjectCountParams = (params) => {
  const object_count = params.get("object_count")
  if (object_count) {
    const [lower, upper] = asRangeInclusive(object_count)
    params.delete("object_count")
    // Either of these may be undefined - need to check, otherwise
    // URLSearchParams sets to string 'undefined'.
    if (lower) {
      params.set("solve_count_gt", lower)
    }
    if (upper) {
      params.set("solve_count_lt", upper)
    }
  }
}

/**
 * Get an observation by Id
 * @param observationId
 * @returns {Promise<any>}
 */
export const getObservationById = async (observationId) => {
  let resp = await fetch(`${API_BASE_URL}/api/observations/${observationId}`, {
    method: "GET",
    mode: "cors",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    redirect: "error",
  })

  resp = handleSignIn(resp)
  return resp.json()
}

/** Produces a URL that that will download a CSV file
 * of the observations, filtered by the given
 * filter. */
export const getObservationsCSVURL = (filter, authToken) => {
  const params = new URLSearchParams()
  updateSearchParamsWithFilters(params, filter)
  concretiseParams(params)
  params.set("format", "csv")
  return `${API_BASE_URL}/api/observations/download?${params.toString()}${authToken ? "&token=" + encodeURIComponent(authToken) : ""
    }`
}

/** Produces a (potentially large) URL to obtain a zipped file bundle
 * across multiple observations. */
export const getObservationsBundleURL = (
  rawFileName, // e.g. "event_stream"
  transcodeAs, // e.g. "csv"
  observationIDs // Array<ObservationID>
) =>
  `${API_BASE_URL}/api/observations/file-bundle/${encodeURIComponent(
    (observationIDs || []).join(",")
  )}/${rawFileName}/as-${transcodeAs}?token=${encodeURIComponent(getToken())}`

/**
 * Tag an observation
 * @param observationId
 * @param tagName the tag name e.g. moon
 * @returns {Promise<any>}
 */
export const tagObservationById = async (request = {}) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/observations/${request.observationId}/tags`,
    {
      method: "PUT",
      mode: "cors",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        name: request.name,
        value: request.value,
        manual: true, // Applied manually on UI
        _etag: request._etag,
      }),
      redirect: "error",
    }
  )

  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Un-tag an observation
 * @param observationId
 * @param tagName the tag name e.g. moon
 * @returns {Promise<undefined>} - no payload upon successful tag removal
 */
export const unTagObservationById = async (request = {}) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/observations/${request.observationId}/tags/${request.name}`,
    {
      method: "DELETE",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        name: request.name,
        value: request.value,
        _etag: request._etag,
      }),
      redirect: "error",
    }
  )
  resp = handleSignIn(resp)
}

/**
 * Accept or decline an observation and if accepted pick the track that matches the object for the specified observation
 *
 * @param request e.g. { "accepted": true, "track": "A" }
 * @returns {Promise<any>}
 */
export const acceptObservation = async (request = {}) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/observations/${request.observationId}/acceptance`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        accepted: request?.accepted,
        track: request?.track,
        _etag: request._etag,
      }),
      redirect: "error",
    }
  )
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Get an observation stats for a set of filters
 * @param observationId
 * @returns {Promise<any>}
 */
export const getObservationStats = async ({ filter = {} }) => {
  // Build parameter set
  const params = new URLSearchParams()
  // Add the relevant filters
  updateSearchParamsWithFilters(params, filter)
  // Replace any relative ones with absolute ones

  concretiseParams(params)

  let resp = await fetch(
    `${API_BASE_URL}/api/observations/stats?${params.toString()}`,
    {
      method: "GET",
      mode: "cors",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      redirect: "error",
    }
  )

  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Identify all the observations matching the given filter.
 * Note: Does not support pagination, as the intention is to return the
 * entire list of IDs for UI purposes such as navigating through large
 * lists of observations.
 * @param filers  // filters to apply
 * @returns {Promise<any>}
 */
export const getObservationIDs = async ({ filter = {} }) => {
  const params = observationListSearchParams(filter)
  let resp = await fetch(
    `${API_BASE_URL}/api/observations/ids?${params.toString()}`,
    {
      method: "GET",
      mode: "cors",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      redirect: "error",
    }
  )

  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Archive an observation
 *
 * @param id  // id of the Observation we want to archive
 * @returns {Promise<any>}
 */
export const archiveObservation = async (id) => {
  let resp = await fetch(`${API_BASE_URL}/api/observations/${id}`, {
    method: "DELETE",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    cache: "no-cache",
    redirect: "error",
  })
  resp = handleSignIn(resp)
  return resp.json()
}

/** Constructs URLSearchParams from the given Filter object in order
 * to request a list of matching observations from the back-end */
const observationListSearchParams = (filter = {}) => {
  // Build parameter set
  const params = new URLSearchParams()
  // Add the relevant filters
  updateSearchParamsWithFilters(params, filter)
  // Replace any relative ones with absolute ones
  concretiseParams(params)
  // Use-case-specific parameters expansion
  expandObjectCountParams(params)
  return params
}
